import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { GOOGLE_SHEETS_CONFIG } from '../config/googleSheets'

// Глобальный кэш для поиска
const searchCache = new Map<string, any>()
const SEARCH_CACHE_DURATION = 15 * 60 * 1000 // 15 минут в миллисекундах

// Функция для получения данных из кэша поиска
const getSearchCachedData = (cacheKey: string): any => {
  const cached = searchCache.get(cacheKey)
  if (!cached) return null
  
  const now = Date.now()
  if (now - cached.timestamp > SEARCH_CACHE_DURATION) {
    // Кэш устарел, удаляем его
    searchCache.delete(cacheKey)
    return null
  }
  
  return cached.data
}

// Функция для сохранения данных в кэш поиска
const setSearchCachedData = (cacheKey: string, data: any) => {
  searchCache.set(cacheKey, {
    data,
    timestamp: Date.now()
  })
}

interface SearchResult {
  section: string
  sectionTitle: string
  row: string[]
  headers: string[]
  relevance: number
  matchedColumns: number[]
}

interface SearchModalProps {
  isOpen: boolean
  onClose: () => void
  searchQuery: string
}

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose, searchQuery }) => {
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Функция для вычисления расстояния Левенштейна (для поиска с ошибками)
  const levenshteinDistance = (str1: string, str2: string): number => {
    const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null))
    
    for (let i = 0; i <= str1.length; i++) matrix[0][i] = i
    for (let j = 0; j <= str2.length; j++) matrix[j][0] = j
    
    for (let j = 1; j <= str2.length; j++) {
      for (let i = 1; i <= str1.length; i++) {
        const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1
        matrix[j][i] = Math.min(
          matrix[j][i - 1] + 1,
          matrix[j - 1][i] + 1,
          matrix[j - 1][i - 1] + indicator
        )
      }
    }
    
    return matrix[str2.length][str1.length]
  }

  // Функция для вычисления релевантности
  const calculateRelevance = (text: string, query: string): number => {
    const lowerText = text.toLowerCase()
    const lowerQuery = query.toLowerCase()
    
    // Точное совпадение
    if (lowerText.includes(lowerQuery)) return 100
    
    // Нормализация чисел (убираем пробелы, точки, запятые)
    const normalizeNumber = (str: string) => str.replace(/[\s.,]/g, '')
    const normalizedText = normalizeNumber(lowerText)
    const normalizedQuery = normalizeNumber(lowerQuery)
    
    // Поиск по нормализованным числам
    if (normalizedText.includes(normalizedQuery) && /^\d+$/.test(normalizedQuery)) {
      return 95
    }
    
    // Поиск чисел с разными форматами
    const textNumbers = lowerText.match(/\d+[\s.,]?\d*/g) || []
    const queryNumbers = lowerQuery.match(/\d+[\s.,]?\d*/g) || []
    
    for (const queryNum of queryNumbers) {
      for (const textNum of textNumbers) {
        const normQueryNum = normalizeNumber(queryNum)
        const normTextNum = normalizeNumber(textNum)
        if (normTextNum.includes(normQueryNum) || normQueryNum.includes(normTextNum)) {
          return 90
        }
      }
    }
    
    // Частичное совпадение
    const words = lowerQuery.split(' ').filter(word => word.length > 2)
    let partialMatches = 0
    words.forEach(word => {
      if (lowerText.includes(word)) partialMatches++
    })
    if (partialMatches > 0) return 50 + (partialMatches / words.length) * 30
    
    // Поиск с ошибками по отдельным словам
    const textWords = lowerText.split(/\s+/)
    const queryWords = lowerQuery.split(/\s+/)
    
    let bestWordSimilarity = 0
    
    for (const queryWord of queryWords) {
      for (const textWord of textWords) {
        if (textWord.length < 3) continue // Пропускаем короткие слова
        
        const distance = levenshteinDistance(textWord, queryWord)
        const maxLength = Math.max(textWord.length, queryWord.length)
        const similarity = 1 - (distance / maxLength)
        
        if (similarity > bestWordSimilarity) {
          bestWordSimilarity = similarity
        }
      }
    }
    
    // Если нашли похожее слово
    if (bestWordSimilarity > 0.6) {
      return bestWordSimilarity * 70
    }
    
    // Поиск с ошибками по всему тексту (fallback)
    const distance = levenshteinDistance(lowerText, lowerQuery)
    const maxLength = Math.max(lowerText.length, lowerQuery.length)
    const similarity = 1 - (distance / maxLength)
    
    if (similarity > 0.5) return similarity * 60
    
    return 0
  }

  // Функция поиска
  const performSearch = async () => {
    if (!searchQuery.trim()) return
    
    setLoading(true)
    setError(null)
    
    // Создаем ключ кэша для поиска
    const searchCacheKey = `search_${searchQuery.trim().toLowerCase()}`
    
    // Проверяем кэш поиска
    const cachedResults = getSearchCachedData(searchCacheKey)
    if (cachedResults) {
      setResults(cachedResults)
      setLoading(false)
      return
    }
    
    try {
      const sections = [
        { id: 'ak', title: 'Административный кодекс', ranges: ['АК.УК.ДК.ПК!C3:E13'] },
        { id: 'uk', title: 'Уголовный кодекс', ranges: ['АК.УК.ДК.ПК!G3:L47', 'АК.УК.ДК.ПК!G49:L54'] },
        { id: 'dk', title: 'Дорожный кодекс', ranges: ['АК.УК.ДК.ПК!C15:E25'] }
        // Исключена секция 'additional' (Дополнительная информация) из поиска
      ]
      
      const allResults: SearchResult[] = []
      
             for (const section of sections) {
         let sectionData: any = null
         
                   // Получаем данные из API для всех диапазонов секции
          for (const range of section.ranges) {
            try {
              const url = `https://sheets.googleapis.com/v4/spreadsheets/${GOOGLE_SHEETS_CONFIG.SPREADSHEET_ID}/values/${range}?key=${GOOGLE_SHEETS_CONFIG.API_KEY}`
              const response = await fetch(url)
              
              if (response.ok) {
                const result = await response.json()
                
                if (result.values && result.values.length > 0) {
                  if (!sectionData) {
                    sectionData = { headers: result.values[0], rows: [] }
                    sectionData.rows.push(...result.values.slice(1))
                  } else {
                    // Если это не первый диапазон, проверяем, есть ли заголовки
                    const firstRow = result.values[0]
                    const hasHeaders = firstRow.some((cell: any) => 
                      typeof cell === 'string' && 
                      (cell.toLowerCase().includes('статья') || 
                       cell.toLowerCase().includes('преступление') || 
                       cell.toLowerCase().includes('наказание') ||
                       cell.toLowerCase().includes('название') ||
                       cell.toLowerCase().includes('описание'))
                    )
                    
                    if (hasHeaders) {
                      // Если первая строка содержит заголовки, добавляем остальные строки
                      sectionData.rows.push(...result.values.slice(1))
                    } else {
                      // Если первая строка не содержит заголовки, добавляем все строки
                      sectionData.rows.push(...result.values)
                    }
                  }
                }
              }
            } catch (error) {
              // Ошибка при запросе к диапазону
            }
          }
         
                   // Обрабатываем данные секции только если получили их из API
          if (sectionData && sectionData.rows && sectionData.rows.length > 0) {
            // Обработка для всех секций (исключена секция 'additional')
            sectionData.rows.forEach((row: string[]) => {
              let maxRelevance = 0
              const matchedColumns: number[] = []
              
              row.forEach((cell, cellIndex) => {
                const relevance = calculateRelevance(cell, searchQuery)
                if (relevance > 0) {
                  matchedColumns.push(cellIndex)
                  maxRelevance = Math.max(maxRelevance, relevance)
                }
              })
              
              if (maxRelevance > 0) {
                allResults.push({
                  section: section.id,
                  sectionTitle: section.title,
                  row,
                  headers: sectionData.headers,
                  relevance: maxRelevance,
                  matchedColumns
                })
              }
            })
          }
       }
      
             // Сортировка по релевантности
       allResults.sort((a, b) => b.relevance - a.relevance)
       
       setResults(allResults)
       setSearchCachedData(searchCacheKey, allResults)
    } catch (err) {
      setError('Ошибка при поиске')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen && searchQuery.trim()) {
      performSearch()
    }
  }, [isOpen, searchQuery])

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[10003] backdrop-blur-md bg-black/70 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <div 
            className="relative max-w-6xl w-full max-h-[90vh] overflow-y-auto scrollbar-hide bg-white/5 backdrop-blur-sm rounded-2xl border border-white/20 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Кнопка закрытия */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-[10004] bg-white/10 hover:bg-white/20 text-white w-10 h-10 rounded-lg backdrop-blur-sm transition-all duration-200 flex items-center justify-center"
            >
              ✕
            </button>
            
            {/* Заголовок */}
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-white mb-2">
                Результаты поиска
              </h2>
              <p className="text-gray-400">
                Поиск: <span className="text-purple-400 font-semibold">"{searchQuery}"</span>
              </p>
            </div>

            {/* Результаты */}
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                <span className="ml-3 text-gray-400">Поиск...</span>
              </div>
            )}

            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400 text-center">
                {error}
              </div>
            )}

            {!loading && !error && results.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <div className="text-6xl mb-4">🔍</div>
                <p className="text-xl">Ничего не найдено</p>
                <p className="text-sm mt-2">Попробуйте изменить запрос</p>
              </div>
            )}

            {!loading && !error && results.length > 0 && (
              <div className="space-y-4">
                <p className="text-gray-400 text-sm">
                  Найдено результатов: <span className="text-white font-semibold">{results.length}</span>
                </p>
                
                {results.map((result, index) => (
                  <motion.div
                    key={`${result.section}-${index}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-4"
                  >
                    {/* Заголовок результата */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          result.section === 'ak' ? 'bg-green-500/20 text-green-300' :
                          result.section === 'uk' ? 'bg-red-500/20 text-red-300' :
                          'bg-blue-500/20 text-blue-300'
                        }`}>
                           {result.sectionTitle}
                         </div>
                        
                      </div>
                    </div>

                    {/* Данные */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {result.row.map((cell, cellIndex) => (
                        <div key={cellIndex} className="space-y-1">
                          <div className="text-xs text-gray-500 font-medium">
                            {result.headers[cellIndex]}
                          </div>
                          <div className={`text-sm p-2 rounded-lg ${
                            result.matchedColumns.includes(cellIndex)
                              ? 'bg-purple-500/20 text-purple-200 border border-purple-500/30'
                              : 'bg-white/5 text-white/80'
                          }`}>
                            {cell === 'TRUE' ? (
                              <div className="flex items-center justify-center">
                                <div className="relative w-5 h-5 bg-gradient-to-br from-green-400 to-green-600 rounded-lg shadow-lg flex items-center justify-center">
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              </div>
                            ) : cell === 'FALSE' ? (
                              <div className="flex items-center justify-center">
                                <div className="relative w-5 h-5 bg-gradient-to-br from-red-400 to-red-600 rounded-lg shadow-lg flex items-center justify-center">
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              </div>
                            ) : (
                              cell
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default SearchModal
